import { AxiosError } from "axios";
import {
  Button,
  CheckBox,
  LoadIndicator,
  Popup,
  TagBox,
  TextBox,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import { EmailRule, RequiredRule, StringLengthRule } from "devextreme-react/validator";
import { ClickEvent } from "devextreme/ui/button";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ServerResponse from "../../components/ServerResponse";
import { RoleLookupDS } from "../../dataSources/RoleLookupDS";
import { reloadGrid, closePopup } from "../../helper/DxHelper";
import { useUserCreation } from "../../hooks/useUserCreation";
import type { User, ValidationResultItem } from "../../types/types";
import { PopupRef } from "devextreme-react/cjs/popup";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { TextBoxRef } from "devextreme-react/cjs/text-box";
import { CheckBoxRef } from "devextreme-react/cjs/check-box";
import { TagBoxRef } from "devextreme-react/cjs/tag-box";

type Props = {
  popupRef: React.RefObject<PopupRef>;
  datagridRef: React.RefObject<DataGridRef<User, string>>;
};

const validationGroupName = "insertUserVal";

export default function Insert({ popupRef, datagridRef }: Props) {
  const firstNameRef = useRef<TextBoxRef>(null);
  const lastNameRef = useRef<TextBoxRef>(null);
  const emailRef = useRef<TextBoxRef>(null);
  const usernameRef = useRef<TextBoxRef>(null);
  const permissionsRef = useRef<TagBoxRef>(null);
  const inactiveRef = useRef<CheckBoxRef>(null);
  const { mutate, data: response, isPending, isError, error, isSuccess } = useUserCreation();
  const [serverErrors, setServerErrors] = useState<ValidationResultItem[] | null>(null);

  function submit(e: ClickEvent) {
    setServerErrors(null);

    if (!e.validationGroup.validate().isValid) return;

    const requestData = {
      firstName: firstNameRef?.current?.instance()?.option("value"),
      lastName: lastNameRef?.current?.instance()?.option("value"),
      email: emailRef?.current?.instance()?.option("value"),
      permissions: permissionsRef?.current?.instance()?.option("value"),
      username: usernameRef?.current?.instance()?.option("value"),
      inactive: inactiveRef?.current?.instance()?.option("value"),
    };
    mutate(requestData);
  }

  function setDisableInputs(state: boolean) {
    firstNameRef.current?.instance().option("disabled", state);
    lastNameRef.current?.instance().option("disabled", state);
    emailRef.current?.instance().option("disabled", state);
    permissionsRef.current?.instance().option("disabled", state);
    usernameRef.current?.instance().option("disabled", state);
    inactiveRef.current?.instance().option("disabled", state);
  }

  function clearForm() {
    firstNameRef?.current?.instance()?.option("value", "");
    lastNameRef?.current?.instance()?.option("value", "");
    emailRef?.current?.instance()?.option("value", "");
    usernameRef?.current?.instance()?.option("value", "");
    permissionsRef?.current?.instance()?.option("value", []);
    inactiveRef?.current?.instance()?.option("value", false);
  }

  useEffect(() => {
    if (isSuccess) {
      if (!response?.validation.items?.length) {
        clearForm();
        toast.success(
          <div>
            <span>
              User {response.data.firstName} {response.data?.lastName} added
            </span>
          </div>,
          {
            type: "success",
            autoClose: 3000,
            closeButton: true,
            closeOnClick: true,
          }
        );
        closePopup(popupRef);
      } else {
        setServerErrors(response?.validation.items);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    setDisableInputs(isPending);
  }, [isPending]);

  return (
    <Popup
      title="Add new user"
      showCloseButton={true}
      ref={popupRef}
      onHiding={() => {
        setServerErrors(null);
        reloadGrid(datagridRef);
      }}
    >
      <form className="grid grid-cols-2 gap-8">
        <TextBox
          label="First Name*"
          labelMode="static"
          ref={firstNameRef}
          stylingMode="outlined"
          valueChangeEvent="keyup"
        >
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"First Name is required"} />
            <StringLengthRule max={50} message={`Max length of first name is 50`} />
          </Validator>
        </TextBox>

        <TextBox
          label="Last Name*"
          labelMode="static"
          ref={lastNameRef}
          stylingMode="outlined"
          valueChangeEvent="keyup"
        >
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Last Name is required"} />
            <StringLengthRule max={50} message={`Max length of last name is 50`} />
          </Validator>
        </TextBox>
        <TextBox
          label="Email Address*"
          labelMode="static"
          ref={emailRef}
          stylingMode="outlined"
          valueChangeEvent="keyup"
        >
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Email Address is required"} />
            <EmailRule message={"Invalid email format"} />
            <StringLengthRule max={128} message={`Max length of email address is 128`} />
          </Validator>
        </TextBox>
        <TextBox label="Username*" labelMode="static" ref={usernameRef} stylingMode="outlined">
          <Validator validationGroup={validationGroupName}>
            <RequiredRule message={"Username is required"} />
            <StringLengthRule max={128} message={`Max length of Username is 128`} />
          </Validator>
        </TextBox>
        <TagBox
          label="Roles"
          labelMode="static"
          ref={permissionsRef}
          dataSource={RoleLookupDS()}
          displayExpr={"name"}
          stylingMode={"outlined"}
          showClearButton={true}
          showSelectionControls={true}
          searchEnabled={true}
          multiline={false}
          selectAllMode="page"
          valueExpr={"id"}
        />

        <div className="mt-4 flex flex-col gap-2">
          <CheckBox ref={inactiveRef} text="Inactive" />
        </div>
      </form>

      <div className="absolute bottom-4 left-4">
        <div className="text-danger">
          {isError && error instanceof AxiosError ? <span>{error.message}</span> : null}
        </div>
        <ValidationSummary validationGroup={validationGroupName} id="summary"></ValidationSummary>
        {serverErrors?.length ? <ServerResponse items={serverErrors} /> : null}
      </div>
      <div className="absolute bottom-4 right-4">
        <Button
          validationGroup={validationGroupName}
          useSubmitBehavior={false}
          type="default"
          height={40}
          width={isPending ? 150 : 100}
          onClick={submit}
        >
          <LoadIndicator
            id="default-btn-loading"
            className="mr-3 inline-block align-middle"
            width={25}
            height={25}
            visible={isPending}
          />
          <span className="dx-button-text">{isPending ? "Creating" : "Create"}</span>
        </Button>
      </div>
    </Popup>
  );
}
